/**
 * Connection helpers built on `navigator.onLine`.
 *
 * The flag is only trustworthy in one direction: `false` means the device has
 * no route out at all, while `true` merely means it is attached to *a* network
 * — a captive portal or a dead uplink still reports true. So it is used to skip
 * work that is certain to fail, never to conclude that a request will succeed.
 */

/** Whether the device is known to have no route out. */
export const isOffline = (): boolean =>
  typeof navigator !== "undefined" && navigator.onLine === false;

/**
 * Resolve as soon as the browser reports a connection again.
 *
 * Returns immediately when already online, so a caller can await it
 * unconditionally before a retry without paying for the common case.
 *
 * @param timeoutMs - Give up after this long.
 * @returns Whether the connection came back before the timeout elapsed.
 */
export const waitForConnection = (timeoutMs: number): Promise<boolean> =>
  new Promise((resolve) => {
    if (!isOffline()) {
      resolve(true);
      return;
    }

    // The controller detaches the listener whichever way this settles.
    const controller = new AbortController();
    const timer = setTimeout(() => {
      controller.abort();
      resolve(false);
    }, timeoutMs);

    window.addEventListener(
      "online",
      () => {
        clearTimeout(timer);
        resolve(true);
      },
      { signal: controller.signal, once: true },
    );
  });

/** Resolve after `ms`. */
export const delay = (ms: number): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));
